import { addFocusedBy, removeFocusedBy } from '@design/utilities/helpers/body-focused-by'
import { createGesture, dragAction } from '@use-gesture/vanilla'
import type { Gesture } from '@use-gesture/vanilla'

export interface SidePanelConfig {
  rootEl?: HTMLElement
  class?: string
  onOpen?: (sidePanel: SidePanel) => void
  onBeforeOpen?: (sidePanel: SidePanel) => void
  onClose?: (sidePanel: SidePanel) => void
  onBeforeClose?: (sidePanel: SidePanel) => void
  onContentSet?: (sidePanel: SidePanel) => void
  onDestroy?: (sidePanel: SidePanel) => void
}

export default class SidePanel {
  config!: SidePanelConfig
  element!: HTMLElement
  isOpened!: boolean
  fadeEl!: HTMLElement
  handleEl!: HTMLElement | null
  wrapperEl!: HTMLElement
  closeEls!: HTMLElement[] | null
  contentEl!: HTMLElement
  gesture!: Gesture

  constructor(element: string | HTMLElement, config: SidePanelConfig = {}) {
    let el: HTMLElement | string | null = element

    if (typeof element === 'string') {
      el = document.querySelector(element) as HTMLElement
    }

    if (!el || typeof el === 'string') {
      return
    }

    this.element = el

    if (this.element.hasAttribute('data-init')) {
      return
    }

    if (!config.rootEl) {
      config.rootEl = this.element.parentNode as HTMLElement
    }

    this.config = config
    this.isOpened = false

    this.setElements()

    if (this.config.class) {
      this.element.classList.add(this.config.class)
    }

    this.initEvents()

    this.element.setAttribute('data-init', 'true')
  }

  setElements(): void {
    const wrapperEl = this.element.querySelector('.o-side-panel__wrapper') as HTMLElement
    const fadeEl = this.element.querySelector('.o-side-panel__fade') as HTMLElement
    const contentEl = this.element.querySelector('.o-side-panel__content') as HTMLElement

    this.wrapperEl = wrapperEl
    this.fadeEl = fadeEl
    this.contentEl = contentEl
    this.handleEl = this.element.querySelector('.o-side-panel__handle')
    this.closeEls = Array.from(this.element.querySelectorAll<HTMLElement>('.o-side-panel__close'))
  }

  getElement(): HTMLElement {
    return this.element
  }

  initEvents(): void {
    if (this.closeEls && this.closeEls.length > 0) {
      this.fadeEl.addEventListener('click', () => {
        this.close()
      })

      this.closeEls.forEach((el) => {
        el.addEventListener('click', () => {
          this.close()
        })
      })

      document.body.addEventListener('keyup', (e) => {
        if (e.keyCode === 27) {
          this.close()
        }
      })
    }

    if (this.handleEl) {
      const Gesture = createGesture([dragAction])
      this.gesture = Gesture(
        this.handleEl,
        {
          onDragEnd: (state) => {
            if (state.direction[1] === 1) {
              this.toggle()
            }
          },
        },
        {
          drag: {
            axis: 'y',
          },
        }
      )
    }

    this.wrapperEl.addEventListener('transitionend', (e) => {
      if (!this.isOpened && (e.propertyName === 'transform' || e.propertyName === 'opacity')) {
        this.element.setAttribute('hidden', 'hidden')

        if (this.config.rootEl !== this.element.parentNode) {
          this.config.rootEl?.append(this.element)
        }

        if (this.config.onClose) {
          this.config.onClose(this)
        }
      }
    })
  }

  open(): SidePanel {
    if (this.isOpened) {
      return this
    }

    addFocusedBy('side-panel')

    if (document.body !== this.element.parentNode) {
      document.body.append(this.element)
    }

    setTimeout(() => {
      this.config.onBeforeOpen?.(this)

      this.element.removeAttribute('hidden')
      // trigger reflow to make sure pending styles are applied
      void this.element.offsetHeight
      this.element.setAttribute('data-open', 'true')
      this.element.focus()
      this.isOpened = true

      this.element.dispatchEvent(new CustomEvent('side-panel.opened'))

      if (this.config.onOpen) {
        this.config.onOpen(this)
      }
    }, 50)

    return this
  }

  close(): SidePanel {
    if (!this.isOpened) {
      return this
    }

    this.config.onBeforeClose?.(this)

    this.element.setAttribute('data-open', 'false')

    removeFocusedBy('side-panel')
    this.isOpened = false

    this.element.dispatchEvent(new CustomEvent('side-panel.closed'))

    return this
  }

  setContent(content: string | HTMLElement): SidePanel {
    if (typeof content === 'string') {
      this.contentEl.innerHTML = content
    } else {
      this.contentEl.appendChild(content)
    }

    if (this.config.onContentSet) {
      this.config.onContentSet(this)
    }

    return this
  }

  toggle(): SidePanel {
    if (this.isOpened) {
      this.close()
    } else {
      this.open()
    }

    return this
  }

  destroy(): void {
    if (this.config.onDestroy) {
      this.config.onDestroy(this)
    }

    this.config.rootEl?.removeChild(this.element)
  }
}
